import DOMPurify from 'dompurify'
import React, { useState } from 'react'
import { Box, TextField } from '@mui/material'
import {
  Button,
  CloseIcon,
  colors,
  DialogContent,
  IconButton,
} from '@resiliatech/birdsong'
import { TaskRecurrenceType, Goal } from 'Generated/graphql-types'
import { ChecklistItemWithResourceInfo } from 'TypeDefs/Goal'
import { useAppState } from 'Context/AppContext/AppContext'
import { Popover } from '@material-ui/core'
import { taskAnalytics } from '../analytics.utils'
import {
  ButtonsContainer,
  CheckboxesContainer,
  Copy,
  DatePickersContainer,
  ErrorMessage,
  errStyles,
  Footer,
  Header,
  HeaderContainer,
  IntervalOptions,
  SubHeader,
  SubHeaderContainer,
  TaskDetailsInputsContainer,
  TaskDueDateInputsContainer,
  TaskModalContainer,
  TaskModalContent,
} from './TaskModalV2.styled'
import {
  initialRecurrenceOptions,
  initialTaskDetails,
  TaskDetailsWithRecurrence,
  UpsertTaskHandler,
  UpdateTaskHandlerArgs,
} from '../types'
import {
  buildRecurrenceMessage,
  getRecurrenceValuesToSendMutation,
} from '../utils'
import { RecurrenceOptions } from './RecurrenceOptions'
import { DueDateInput } from './DueDateInput'
import { StartDateInput } from './StartDateInput'
import { UntilDateInput } from './UntilDateInput'
import { TimeUnitSelectInput } from './TimeUnitSelectInput'
import { FrequencyInput } from './FrequencyInput'
import { RecurrenceSelectInput } from './RecurrenceSelectInput'
import { AssignOwnerSelectInput } from './AssignOwnerSelectInput'
import { LinkInputs } from './LinkInputs'
import { AcademyResourceOptions } from './AcademyResourceOptions'
import {
  HeaderContainer as PopupHeaderContainer,
  TitleSection as PopupTitleSection,
} from '../../ProgressUpdate/ProgressUpdateDialog/ProgressUpdateDialog.styled'
import { AddChartDataModalContainer } from '../../AddChartDataModal/AddChartDataModal.styled'

type TaskModalV2Props = {
  isDuplicating: boolean
  isLoading: boolean
  isTaskModalOpen: boolean
  onCloseModal: () => void
  onUpsertTask: UpsertTaskHandler
  task?: ChecklistItemWithResourceInfo
  goal: Goal
  simple?: boolean
}

export const TaskModalV2 = ({
  isDuplicating,
  isLoading,
  isTaskModalOpen,
  onCloseModal,
  onUpsertTask,
  task,
  goal,
  simple,
}: TaskModalV2Props) => {
  const [titleError, setTitleError] = useState('')
  const [untilDateError, setUntilDateError] = useState('')
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isRecurring, setIsRecurring] = useState(false)
  const [taskData, setTaskData] = useState<TaskDetailsWithRecurrence>({
    ...initialRecurrenceOptions,
    ...initialTaskDetails,
    ...task,
  })

  const {
    nonprofitOrgConfig: {
      platformPlan,
      orgId: nonprofitOrgId,
      funderOrgId,
      role: userRole,
    },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()

  const baseEventProps = {
    assignerEmail: userEmail,
    assignerId: userId,
    funderOrgId,
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!goal.FunderOrgToSharedGoal,
    nonprofitOrgId,
    platformPlan,
    templateLibraryId: goal.templateLibraryId || null,
    userId,
    userRole,
  }

  const {
    dueDate,
    dayOfMonth,
    dayOfWeek,
    frequency,
    weekOfMonth,
    startDate,
    recurrenceType,
    timeUnitSubType,
    endDate,
    title,
  } = taskData

  const handleErrorFeedback = (
    message: string,
    errFunc: (msg: string) => void
  ) => {
    errFunc(message)
    setIsSubmitDisabled(true)
  }

  const resetErrorStates = () => {
    setTitleError('')
    setUntilDateError('')
    setIsSubmitDisabled(false)
  }

  const handleUpdateTaskData = <K extends keyof TaskDetailsWithRecurrence>({
    key,
    value,
  }: UpdateTaskHandlerArgs<K>) => {
    resetErrorStates()
    setTaskData((prevTaskData) => ({
      ...prevTaskData,
      [key]: value,
    }))
  }

  const handleUpdateTimeUnit = (value: TaskRecurrenceType) => {
    // Requirements called for limiting quarterly recurrence value to one
    if (value === TaskRecurrenceType.Quarterly) {
      handleUpdateTaskData({ key: 'frequency', value: '1' })
    }

    handleUpdateTaskData({ key: 'recurrenceType', value })
  }

  const handleCancel = () => {
    taskAnalytics.dispatchGoalTaskCancelClicked(taskData, {
      ...baseEventProps,
      assigneeUserEmail: null,
      dueDate: taskData.dueDate ?? null,
      ownerId: taskData.owner?.id ?? null,
      taskId: taskData.id,
      taskUpdatedDate: new Date().toISOString(),
    })
    onCloseModal()
  }

  const handleSubmit = async () => {
    const hasUntilDateError = !endDate || !!(startDate && endDate < startDate)

    if (!title.trim()) {
      handleErrorFeedback('Title is required', setTitleError)
      return
    }

    if (isRecurring && hasUntilDateError) {
      handleErrorFeedback(
        `An Until Date is required for all recurring tasks to indicate
        when the last task will be scheduled. This date must be after the Start Date.`,
        setUntilDateError
      )
      return
    }

    try {
      const shouldFireCreateEvent = !task

      if (isRecurring && startDate && endDate) {
        const recurrenceOptions = {
          dayOfMonth,
          dayOfWeek,
          frequency: Number(taskData.frequency),
          weekOfMonth,
          startDate,
          recurrenceType,
          endDate,
          timeUnitSubType,
        }

        const recurrenceOptionsToSendMutation =
          getRecurrenceValuesToSendMutation(recurrenceOptions)

        await onUpsertTask(
          taskData,
          recurrenceOptionsToSendMutation,
          true,
          true,
          isRecurring
        )
      } else {
        await onUpsertTask(taskData, undefined, shouldFireCreateEvent, true)
      }
      onCloseModal()
    } catch {
      handleErrorFeedback(
        'An error occurred. Please try again.',
        setUntilDateError
      )
    }
  }

  const recurrenceMessage = buildRecurrenceMessage(taskData)

  let modalAction = 'Add'
  if (isDuplicating) modalAction = 'Duplicate'
  else if (task) modalAction = 'Edit'

  let taskDueDateCopy = ''
  if (task?.checklistItemGroupId)
    taskDueDateCopy = `This task is part of a series; however, changing its due date will not
      impact other tasks within the series. Only this task's due date will change.`
  else if (isRecurring) taskDueDateCopy = recurrenceMessage
  else
    taskDueDateCopy = `Setting due dates holds you, and your team accountable. Setting recurring
      dates helps everyone remember and plan for expected, upcoming, or routine tasks.`

  const titleInputStyles = { width: '100%', ...(titleError ? errStyles : {}) }
  const modalContent = (
    <>
      {!simple && (
        <HeaderContainer>
          <Header>{modalAction} Task</Header>
          <Copy>
            {isDuplicating
              ? `Duplicating this task will create a new task. This will not change or affect the original task that is being duplicated.
               Tasks should be related to this goal and they should be discrete pieces of work or things to do that can be assigned to a member of your team.`
              : `Tasks should be related to this goal and they should be discrete
            pieces of work or things to do that can be assigned to a member of
            your team.`}
          </Copy>
        </HeaderContainer>
      )}
      <SubHeaderContainer>
        <SubHeader>Task Details</SubHeader>
        <Copy>
          Tasks should be assigned to a member of your team to increase
          accountability and ensure you are working towards your goals.
        </Copy>
        <TaskDetailsInputsContainer>
          <Box>
            <TextField
              required
              id='task-modal-v2-title-input'
              label='Name this Task'
              value={taskData.title}
              onChange={({ target }) =>
                handleUpdateTaskData({ key: 'title', value: target.value })
              }
              sx={titleInputStyles}
            />
            {titleError && (
              <ErrorMessage color={colors.feedback.errorDark}>
                {titleError}
              </ErrorMessage>
            )}
          </Box>
          <AssignOwnerSelectInput
            ownerId={taskData.owner?.id}
            onUpdateOptions={handleUpdateTaskData}
          />
          <CheckboxesContainer>
            <AcademyResourceOptions
              resource={taskData.resource}
              onUpdateOptions={handleUpdateTaskData}
            />
            <LinkInputs
              linkText={taskData.linkText || ''}
              linkUrl={taskData.linkUrl || ''}
              onUpdateOptions={handleUpdateTaskData}
            />
          </CheckboxesContainer>
        </TaskDetailsInputsContainer>
      </SubHeaderContainer>
      <SubHeaderContainer>
        <SubHeader>Task Due Date</SubHeader>
        <Copy
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __html: DOMPurify.sanitize(taskDueDateCopy),
          }}
        />
        <TaskDueDateInputsContainer>
          {(!task || isDuplicating) && (
            <RecurrenceSelectInput
              recurrence={isRecurring}
              onSetRecurrence={setIsRecurring}
            />
          )}
          {isRecurring && (
            <>
              <IntervalOptions>
                <FrequencyInput
                  recurrenceType={recurrenceType}
                  frequency={frequency}
                  onUpdateOptions={handleUpdateTaskData}
                />
                <TimeUnitSelectInput
                  recurrenceType={recurrenceType}
                  onUpdateInterval={handleUpdateTimeUnit}
                />
              </IntervalOptions>
              {recurrenceType !== TaskRecurrenceType.Annually && (
                <RecurrenceOptions
                  dayOfMonth={dayOfMonth}
                  dayOfWeek={dayOfWeek}
                  recurrenceType={recurrenceType}
                  timeUnitSubType={timeUnitSubType}
                  onUpdateOptions={handleUpdateTaskData}
                  weekOfMonth={weekOfMonth}
                />
              )}
              <DatePickersContainer>
                <StartDateInput
                  startDate={startDate}
                  onUpdateOptions={handleUpdateTaskData}
                />
                <UntilDateInput
                  startDate={startDate}
                  endDate={endDate}
                  onUpdateOptions={handleUpdateTaskData}
                  error={untilDateError}
                />
              </DatePickersContainer>
            </>
          )}
          {!isRecurring && (
            <DueDateInput
              dueDate={dueDate}
              onUpdateOptions={handleUpdateTaskData}
            />
          )}
        </TaskDueDateInputsContainer>
      </SubHeaderContainer>
      <Footer style={{ marginTop: '20px' }}>
        <ButtonsContainer>
          <Button
            onClick={handleCancel}
            type='button'
            variant='tertiary'
            label={task ? 'Do Not Edit' : 'Do Not Add'}
          />
          <Button
            isDisabled={isSubmitDisabled}
            isLoading={isLoading}
            onClick={handleSubmit}
            type='button'
            variant='primary'
            label={task && !isDuplicating ? 'Save Changes' : 'Add Task'}
          />
        </ButtonsContainer>
      </Footer>
    </>
  )

  return simple ? (
    <Popover
      open={isTaskModalOpen}
      onClose={onCloseModal}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      BackdropProps={{
        invisible: true,
        style: { backgroundColor: 'rgba(26, 26, 26, 0.5)' },
      }}
    >
      <AddChartDataModalContainer>
        <PopupHeaderContainer>
          <PopupTitleSection>{modalAction} Task</PopupTitleSection>
          <IconButton onClick={onCloseModal} size='small'>
            <CloseIcon />
          </IconButton>
        </PopupHeaderContainer>
        <DialogContent>
          <Copy>
            {isDuplicating
              ? `Duplicating this task will create a new task. This will not change or affect the original task that is being duplicated.
               Tasks should be related to this goal and they should be discrete pieces of work or things to do that can be assigned to a member of your team.`
              : `Tasks should be related to this goal and they should be discrete
            pieces of work or things to do that can be assigned to a member of
            your team.`}
          </Copy>
          {modalContent}
        </DialogContent>
      </AddChartDataModalContainer>
    </Popover>
  ) : (
    <TaskModalContainer
      open={isTaskModalOpen}
      onClose={onCloseModal}
      disableEnforceFocus
    >
      <TaskModalContent>{modalContent}</TaskModalContent>
    </TaskModalContainer>
  )
}
